import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import { GenericTable, TableModel } from 'components/table/IdmTable';
import { CapacityPlanRunSummary } from 'core/graphql/CapacityPlanning/models';
import { formatDateTime } from 'core/utils/DateUtils';
import RunStatusBadge from './RunStatusBadge';

export interface RunsListProps {
  /** Newest first, as sorted by the page. */
  runs: CapacityPlanRunSummary[];
  isLoading: boolean;
  onOpenRun: (runId: string) => void;
}

/** "48 / 50 geocoded" - counts only, never a row's content. */
const geocodingSummary = (run: CapacityPlanRunSummary) =>
  `${run.geocodedDeliveries} / ${run.totalDeliveries} geocoded`;

const runsTableModel = (
  onOpenRun: (runId: string) => void,
): TableModel<CapacityPlanRunSummary> => ({
  columns: [
    {
      title: 'File',
      width: 24,
      align: 'left',
      html: (run) => <span className="text-sm">{run.fileName}</span>,
    },
    {
      title: 'Status',
      width: 12,
      align: 'left',
      html: (run) => (
        <RunStatusBadge
          status={run.status}
          failedSolves={run.solveProgress.failed}
        />
      ),
    },
    {
      title: 'Deliveries',
      width: 10,
      align: 'right',
      html: (run) => <span className="text-sm">{run.totalDeliveries}</span>,
    },
    {
      title: 'Geocoding',
      width: 20,
      align: 'left',
      html: (run) => <span className="text-sm">{geocodingSummary(run)}</span>,
    },
    {
      title: 'Created',
      width: 18,
      align: 'left',
      html: (run) => (
        <div className="flex flex-col">
          <span className="text-sm">
            {formatDateTime(new Date(run.createdAt))}
          </span>
          <span className="text-xs text-light-black dark:text-sublte-white">
            {run.createdBy}
          </span>
        </div>
      ),
    },
    {
      title: 'Drivers',
      width: 8,
      align: 'right',
      html: (run) => <span className="text-sm">{run.driverCount ?? '—'}</span>,
    },
    {
      title: '',
      width: 8,
      align: 'right',
      html: (run) => (
        <DesignButton
          variant="secondary"
          size="sm"
          onClickHandler={() => onOpenRun(run.id)}
        >
          Open
        </DesignButton>
      ),
    },
  ],
});

/** The runs of the selected Agency, shown while no Run is open. */
const RunsList = ({ runs, isLoading, onOpenRun }: RunsListProps) => (
  <div className="flex flex-col gap-2">
    <DesignCard title="Recent runs">
      {runs.length === 0 && !isLoading ? (
        <p className="text-sm">
          No runs yet. Use New run to upload a deliveries file.
        </p>
      ) : (
        <div className="flex">
          <GenericTable
            framed
            model={runsTableModel(onOpenRun)}
            elements={isLoading ? [] : runs}
            pageNumber={1}
            totalElements={1}
            showPaging={false}
            pageChanged={() => undefined}
            onRowClick={(run) => onOpenRun(run.id)}
          />
        </div>
      )}
    </DesignCard>
    <p className="text-sm text-light-black dark:text-sublte-white">
      Open a run to see its deliveries and result. The run stays in the page
      URL, so the link can be shared or refreshed.
    </p>
  </div>
);

export default RunsList;
